import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const PersonalDayNumberCalculator = () => {
  const [birthDate, setBirthDate] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [personalDayNumbers, setPersonalDayNumbers] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  // Helper function to reduce numbers to single digit
  const reduceNumber = (num) => {
    while (num > 9 && ![11, 22, 33].includes(num)) {
      num = num.toString().split('').reduce((sum, digit) => sum + +digit, 0);
    }
    return num;
  };

  // Helper function to calculate Personal Day Number
  const calculatePersonalDayNumber = () => {
    if (!birthDate.trim() || !selectedDate.trim()) {
      setError('Please enter both birth date and selected date');
      return;
    }

    const [birthYear, birthMonth, birthDay] = birthDate.split('-');
    const [selectedYear, selectedMonth, selectedDay] = selectedDate.split('-');

    // Calculate Personal Day Number
    const personalDayNumber = reduceNumber(
      parseInt(birthDay) + 
      parseInt(selectedDay) + 
      parseInt(selectedMonth) + 
      parseInt(selectedYear)
    );

    // Calculate Personal Hour Numbers
    const hours = [];
    for (let hour = 1; hour <= 24; hour++) {
      const personalHourNumber = reduceNumber(
        hour + 
        parseInt(selectedDay) + 
        parseInt(selectedMonth) + 
        parseInt(selectedYear)
      );
      hours.push({
        hour: hour,
        number: personalHourNumber
      });
    }

    // Calculate Personal Minute Numbers for the first hour
    const minutes = [];
    for (let minute = 0; minute < 60; minute += 15) { // Showing every 15 minutes
      const personalMinuteNumber = reduceNumber(
        minute + 
        1 + // First hour
        parseInt(selectedDay) + 
        parseInt(selectedMonth) + 
        parseInt(selectedYear)
      );
      minutes.push({
        minute: minute,
        number: personalMinuteNumber
      });
    }

    setPersonalDayNumbers({
      personalDayNumber,
      hours,
      minutes,
      selectedDate
    });

    // Generate chart data
    setChartData({
      labels: hours.map(h => h.hour),
      datasets: [{
        label: 'Personal Hour Numbers',
        data: hours.map(h => h.number),
        backgroundColor: [
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 99, 132, 0.5)',
          'rgba(75, 192, 192, 0.5)',
          'rgba(153, 102, 255, 0.5)',
          'rgba(255, 159, 64, 0.5)'
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)'
        ],
        borderWidth: 1
      }]
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'birthDate') {
      setBirthDate(value);
    } else if (name === 'selectedDate') {
      setSelectedDate(value);
    }
    setError('');
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Personal Day Number Calculator | Discover Your Daily Cycles</title>
        <meta name="description" content="Calculate your Personal Day Numbers using our Numerology Calculator. Understand the daily cycles and themes that influence your life." />
        <meta name="keywords" content="personal day numbers, numerology calculator, daily cycles, life themes, personal growth" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">📅</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Personal Day Number Calculator</h1>
            <p className="text-lg text-purple-600 font-semibold">Discover Your Daily Cycles</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          calculatePersonalDayNumber();
        }} className="space-y-6">
          <div className="input-group">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="birthDate">
              Birth Date:
            </label>
            <input
              type="date"
              id="birthDate"
              name="birthDate"
              value={birthDate}
              onChange={handleInputChange}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            />
          </div>

          <div className="input-group">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="selectedDate">
              Date to Calculate:
            </label>
            <input
              type="date"
              id="selectedDate"
              name="selectedDate"
              value={selectedDate}
              onChange={handleInputChange}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
          >
            Calculate Personal Day Number
          </button>

          {error && (
            <div className="text-red-500 text-center mt-4">
              {error}
            </div>
          )}

          {personalDayNumbers && (
            <div className="results-section mt-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Personal Day Numbers</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 bg-white rounded-lg shadow-md">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-purple-600 mb-2">
                      {personalDayNumbers.personalDayNumber}
                    </div>
                    <p className="text-gray-700">Personal Day Number for {personalDayNumbers.selectedDate}</p>
                  </div>
                </div>
              </div>

              {chartData && (
                <div className="space-y-8 mt-12">
                  <div>
                    <h4 className="text-xl font-semibold mb-4">Hourly Personal Numbers</h4>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            title: {
                              display: true,
                              text: 'Hourly Personal Numbers for Day'
                            }
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-xl">
                    <h4 className="text-xl font-semibold mb-4">Hourly Breakdown</h4>
                    <div className="space-y-4">
                      {personalDayNumbers.hours.map((hour, index) => (
                        <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                          <p className="font-medium">Hour {hour.hour}:</p>
                          <p className="text-purple-600 text-xl">{hour.number}</p>
                          <p className="text-sm text-gray-600">Personal Hour Number</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-xl">
                    <h4 className="text-xl font-semibold mb-4">First Hour Minute Breakdown</h4>
                    <div className="space-y-4">
                      {personalDayNumbers.minutes.map((minute, index) => (
                        <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                          <p className="font-medium">Minute {minute.minute}:</p>
                          <p className="text-purple-600 text-xl">{minute.number}</p>
                          <p className="text-sm text-gray-600">Personal Minute Number</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </form>
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Examples & Interpretations <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 1: Day of Growth</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Birth Date: 1990-03-15</p>
                <p className="font-medium mb-2">Date: 2025-03-28</p>
                <p className="text-purple-600 text-2xl mb-2">Personal Day Number: 8</p>
                <p className="mt-4 text-gray-700">
                  Interpretation: This is a day of material growth and financial opportunities. Focus on career advancement and financial planning.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 2: Day of Communication</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Birth Date: 1985-07-22</p>
                <p className="font-medium mb-2">Date: 2025-03-28</p>
                <p className="text-purple-600 text-2xl mb-2">Personal Day Number: 3</p>
                <p className="mt-4 text-gray-700">
                  Interpretation: This is a day of creativity and communication. Embrace new ideas and social connections.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 3: Day of Reflection</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Birth Date: 1995-11-08</p>
                <p className="font-medium mb-2">Date: 2025-03-28</p>
                <p className="text-purple-600 text-2xl mb-2">Personal Day Number: 9</p>
                <p className="mt-4 text-gray-700">
                  Interpretation: This is a day of completion and letting go. Reflect on past experiences and prepare for new beginnings.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Personal Day Number Meanings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold mb-2">1 - New Beginnings</h4>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Leadership opportunities</li>
                    <li>Personal growth</li>
                    <li>New projects</li>
                    <li>Assertiveness</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold mb-2">2 - Partnership</h4>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Teamwork</li>
                    <li>Relationships</li>
                    <li>Balance</li>
                    <li>Harmony</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate your Personal Day Numbers:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 1: Enter Your Birth Date</h3>
              <p className="text-gray-700">
                Input your complete birth date in the format YYYY-MM-DD.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 2: Enter Date to Calculate</h3>
              <p className="text-gray-700">
                Choose the date you want to analyze.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 3: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate" button to see your Personal Day Number and hourly breakdown.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 4: Understand Results</h3>
              <p className="text-gray-700">
                Review your Personal Day Number and hourly numbers to understand the themes and opportunities for the selected day.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What are Personal Day Numbers?</h3>
              <p className="text-gray-700">
                Personal Day Numbers in numerology reveal the themes and opportunities that will influence your life in a particular day.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How are they calculated?</h3>
              <p className="text-gray-700">
                They are calculated by adding your birth day, the day you want to analyze, the month, and the year, then reducing the sum to a single digit.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What do they reveal?</h3>
              <p className="text-gray-700">
                They show the daily cycles and themes that will influence your life, helping you plan and make the most of opportunities.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Accurate Personal Day Number calculation</li>
                <li>Hourly and minute number breakdown</li>
                <li>Visual representation of daily cycles</li>
                <li>Clean and intuitive interface</li>
                <li>Detailed explanation of results</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Understand daily themes and opportunities</li>
                <li>Plan your day effectively</li>
                <li>Make informed decisions</li>
                <li>Maximize personal growth</li>
                <li>Enhance self-awareness</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalDayNumberCalculator;
